'use client';

import React, { FC, useRef, useEffect } from 'react';

import MuxPlayer from '@mux/mux-player-react';
import classNames from 'classnames';

import { ANONYMOUS } from '@/utils/dataLayer';
import { useA11yState } from '@/hooks/useA11yState';

import styles from './MuxVideo.module.scss';

interface MuxVideoProps {
  playbackId: string;
  poster?: string;
  title: string;
  autoplay?: boolean;
  loop?: boolean;
  muted?: boolean;
  playsinline?: boolean;
  controls?: boolean;
  onReady?: () => void;
}

const MuxVideo: FC<MuxVideoProps> = ({
  playbackId,
  poster,
  title,
  autoplay = false,
  loop = true,
  muted = true,
  playsinline = true,
  controls = false,
  onReady,
}) => {
  const { isVideoAccessible } = useA11yState();
  const playerRef = useRef<any>(null);

  // Handle play/pause based on accessibility state
  useEffect(() => {
    const player = playerRef.current;
    if (!player) return;

    const shouldPauseForAccessibility = isVideoAccessible && player.pause;
    const shouldAutoplayWhenAccessibilityDisabled =
      !isVideoAccessible && autoplay && player.play;

    if (shouldPauseForAccessibility) {
      player.pause();
    } else if (shouldAutoplayWhenAccessibilityDisabled) {
      player.play().catch(() => {
        // Modern browsers have strict autoplay policies that may prevent videos from automatically playing.
        // Gracefully handle this rejection if it happens.
      });
    }
  }, [isVideoAccessible, autoplay]);

  return (
    <MuxPlayer
      ref={playerRef}
      data-testid="mux-video"
      streamType="on-demand"
      className={classNames(
        styles.muxVideo,
        !controls && !isVideoAccessible && styles.hideControls,
      )}
      playbackId={playbackId}
      poster={poster}
      envKey={process.env.NEXT_PUBLIC_MUX_ENV_KEY}
      metadata={{
        video_id: playbackId,
        video_title: title,
        viewer_user_id: ANONYMOUS,
        env_key: process.env.NEXT_PUBLIC_MUX_ENV_KEY,
        player_name: 'Preview Video Player',
        player_version: '1.0',
        video_content_type: 'trailer',
        video_producer: 'Lovevery',
      }}
      autoPlay={autoplay && !isVideoAccessible}
      playsInline={playsinline}
      muted={muted}
      loop={loop}
      thumbnailTime={0}
      onCanPlay={onReady}
    />
  );
};

export default React.memo(MuxVideo);
